import { useEffect, useMemo, useState } from "react";
import { Check, FileSpreadsheet, FileText, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { defaultSelection, inDocumentOrder, loadSelection, saveSelection } from "@/lib/exportColumns";
import { cn } from "@/lib/utils";

const FORMATS = {
  pdf: { label: "PDF", Icon: FileText, tone: "text-destructive-text" },
  xlsx: { label: "Excel", Icon: FileSpreadsheet, tone: "text-emerald-600 dark:text-emerald-400" },
};

// Past about this many, a PDF row stops fitting the page and every cell wraps.
const WIDE_PDF = 10;

/**
 * Choose the format, then the columns, for one server-rendered document.
 *
 * EventStorii's picker, carried over with one change: the format comes FIRST, because each
 * format remembers its own columns — the page an auditor reads and the sheet HR pivots are
 * different documents and want different columns. Ticks made here and then a switch of format
 * are kept for the rest of this opening, so looking at Excel does not throw away the PDF choice.
 */
export default function ColumnPickerDialog({ open, onClose, docKey, doc, initialFormat, onDownload }) {
  const formats = useMemo(() => {
    const offered = (doc?.formats || []).filter((f) => FORMATS[f]);
    return offered.length ? offered : ["pdf"];
  }, [doc]);
  const [picked, setPicked] = useState(null);
  const [drafts, setDrafts] = useState({});
  // Worked out rather than waited for: a format held only in state is empty for the first
  // frame of every opening, and the list flashed with nothing ticked.
  const format = formats.includes(picked) ? picked : formats.includes(initialFormat) ? initialFormat : formats[0];

  // Cleared on the way OUT, so a Cancel's unsaved ticks are gone before the next opening
  // paints, rather than flashing for a frame and then being replaced.
  useEffect(() => {
    if (open) return;
    setPicked(null);
    setDrafts({});
  }, [open, docKey]);

  // Read again at every opening: what is remembered can change while the dialog is shut (a
  // download from another tab), and a value kept from the last opening would show that one.
  const selected = useMemo(
    () => (doc && open ? drafts[format] ?? loadSelection(docKey, format, doc) : []),
    [drafts, format, docKey, doc, open],
  );

  if (!doc) return null;
  const columns = doc.columns;
  const chosen = new Set(selected);
  const required = new Set(columns.filter((c) => c.required).map((c) => c.key));
  const allChecked = columns.every((c) => chosen.has(c.key));
  const { label: formatLabel, Icon: FormatIcon } = FORMATS[format];

  const choose = (keys) => setDrafts((d) => ({ ...d, [format]: inDocumentOrder(doc, keys) }));
  const toggle = (key) => {
    if (required.has(key)) return;
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key); else next.add(key);
    choose([...next]);
  };
  const download = () => {
    const keys = inDocumentOrder(doc, selected);
    if (!keys.length) return;
    saveSelection(docKey, format, keys, doc);
    onDownload(format, keys);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="column-picker">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle className="leading-snug">Download — {doc.name}</DialogTitle>
          {doc.doc_no && (
            <div className="text-xs text-muted-foreground" data-testid="picker-doc-no">
              {[doc.doc_no, doc.rev_no ? `Rev ${doc.rev_no}` : ""].filter(Boolean).join(" · ")}
            </div>
          )}
          <DialogDescription>Choose the format, then the columns. Your choice is remembered for this document.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          {formats.length > 1 && (
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground" id="picker-format-label">Format</div>
              <ToggleGroup type="single" variant="outline" value={format} aria-labelledby="picker-format-label"
                           onValueChange={(v) => { if (v) setPicked(v); }} className="justify-start">
                {formats.map((f) => {
                  const { label, Icon, tone } = FORMATS[f];
                  return (
                    <ToggleGroupItem key={f} value={f} data-testid={`fmt-${f}`}
                                     className="gap-1.5 px-3 data-[state=on]:border-primary data-[state=on]:bg-primary/10 data-[state=on]:text-foreground">
                      <Icon className={tone} />{label}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <button type="button" onClick={() => choose(allChecked ? [...required] : columns.map((c) => c.key))}
                      aria-pressed={allChecked} data-testid="picker-all"
                      className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary">
                {/* A drawn box, not a <Checkbox>: Radix's Checkbox is itself a <button>, and one
                    button inside another is invalid HTML that a screen reader cannot use. The
                    state is on this button's aria-pressed. */}
                <span aria-hidden
                      className={cn("grid h-4 w-4 shrink-0 place-items-center rounded-sm border",
                                    allChecked ? "border-primary bg-primary text-primary-foreground" : "border-primary")}>
                  {allChecked && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                {allChecked ? "Deselect all" : "Select all"}
              </button>
              <button type="button" onClick={() => choose(defaultSelection(doc, format))} data-testid="picker-reset"
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                <RotateCcw className="h-3.5 w-3.5" /> Reset to default
              </button>
            </div>

            <div className="max-h-[min(18rem,40dvh)] overflow-y-auto rounded-md border border-border divide-y divide-border"
                 role="group" aria-label="Columns" data-testid="picker-columns">
              {columns.map((c) => {
                const locked = required.has(c.key);
                return (
                  <label key={c.key}
                         className={cn("flex items-center gap-2.5 px-3 py-2 text-sm min-w-0",
                                       locked ? "cursor-default bg-muted/30" : "cursor-pointer hover:bg-muted/50")}>
                    <Checkbox checked={locked || chosen.has(c.key)} disabled={locked}
                              onCheckedChange={() => toggle(c.key)} data-testid={`col-${c.key}`} />
                    <span className="min-w-0 flex-1 truncate">{c.label}</span>
                    {locked && (
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Always included
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground" data-testid="picker-count">
              {chosen.size} of {columns.length} columns selected
            </p>
            {format === "pdf" && chosen.size > WIDE_PDF && (
              <p className="text-xs text-muted-foreground" data-testid="picker-wide-hint">
                A wide PDF prints landscape, and past about {WIDE_PDF} columns every cell starts to wrap.
                Excel takes any number of columns.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={download} disabled={chosen.size === 0} data-testid="picker-download">
            <FormatIcon />Download {formatLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
